import axios from 'axios';
import { handleSessionExpired, isAuthFailure } from './sessionExpiry';
import { API_ORIGIN } from '../config/apiBase';

const axiosInst = axios.create({
    // '' on web (same-origin relative URLs, unchanged behavior);
    // https://renants.com inside the native Capacitor app.
    baseURL: API_ORIGIN || undefined,
    withCredentials: true
});

// Helper function to get cookie value
function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
}

// Store CSRF token
let csrfToken = null;

// Fetch CSRF token on initialization
export const initializeCsrf = async () => {
    try {
        const response = await axiosInst.get('/api/csrf-token');
        csrfToken = response.data.csrfToken;
        return csrfToken;
    } catch (error) {
        console.error('Failed to fetch CSRF token:', error.message || error);
        return null;
    }
};

// Request interceptor to add CSRF token to all state-changing requests
axiosInst.interceptors.request.use(
    (config) => {
        // Add CSRF token to POST, PUT, DELETE, PATCH requests
        if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
            // Try to get token from cookie first, fallback to stored token
            const token = getCookie('XSRF-TOKEN') || csrfToken;
            if (token) {
                config.headers['X-CSRF-Token'] = token;
            }
        }
        // Attach the JWT so auth-gated endpoints (messaging, etc.) receive it.
        // This instance only ever talks to our own origin (relative /api URLs),
        // so there's no third-party leak risk. Skips an already-set header.
        const authToken = localStorage.getItem('auth-token');
        if (authToken && config.headers && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${authToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle CSRF token errors
axiosInst.interceptors.response.use(
    (response) => response,
    async (error) => {
        // An expired/invalid JWT is terminal — stop pollers and prompt for a
        // fresh sign-in instead of letting callers retry forever.
        if (isAuthFailure(error.response)) {
            handleSessionExpired();
            return Promise.reject(error);
        }

        // If we get a CSRF error, refresh the token and retry once
        if (error.response && error.response.status === 403 &&
            error.response.data && 
            (error.response.data.error === 'Invalid CSRF token' || 
             error.response.data.error === 'CSRF token missing')) {
            
            // Prevent infinite retry loop
            if (!error.config._retry) {
                error.config._retry = true;
                try {
                    await initializeCsrf();
                    // Retry the original request
                    return axiosInst.request(error.config);
                } catch (retryError) {
                    return Promise.reject(retryError);
                }
            }
        }
        return Promise.reject(error);
    }
);

export default axiosInst;